import { z } from 'zod';
import { ALL_TOKENS, SUPPORTED_CHAINS, SUPPORTED_TOKENS, SEED_BACKUP_METHODS } from '../constants';

export const ChainEnum = z.enum(SUPPORTED_CHAINS);
/** Stablecoins only — keep for endpoints that must never accept natives. */
export const TokenEnum = z.enum(SUPPORTED_TOKENS);
/** Stablecoins + native coins (chain×token combo validated server-side). */
export const AnyTokenEnum = z.enum(ALL_TOKENS);
export const SeedBackupMethodEnum = z.enum(SEED_BACKUP_METHODS);

export const WalletAddressInput = z.object({
  chain: ChainEnum,
  eoaAddress: z.string().min(1),
  smartAccountAddress: z.string().nullable().optional(),
  publicKey: z.string().min(1),
});
export type WalletAddressInput = z.infer<typeof WalletAddressInput>;

export const CreateWalletInput = z.object({
  name: z.string().min(1).max(64),
  seedBackupMethod: SeedBackupMethodEnum,
  addresses: z.array(WalletAddressInput).min(1),
});
export type CreateWalletInput = z.infer<typeof CreateWalletInput>;

export const TransferPrepareInput = z.object({
  walletId: z.string(),
  chain: ChainEnum,
  token: TokenEnum,
  to: z.string().min(1),
  amount: z.string().regex(/^\d+(\.\d+)?$/),
});
export type TransferPrepareInput = z.infer<typeof TransferPrepareInput>;

/**
 * What this send is paying for, so the API can price it under the card fee
 * policy (issue = network fee absorbed, top-up = auto-discounted from our
 * slice). A CLAIM only: the route re-derives everything from the card treasury,
 * the offered issue template or server-owned top-up card, and the transferred
 * amount. A claim that does not hold up is priced as a plain send.
 */
export const CardPaymentClaim = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('issue'),
    templateId: z.number().int().positive(),
    /** Card issue discount code; lowers the issue price this payment is checked against. */
    promoCode: z.string().min(1).max(64).optional(),
    /** Initial deposit bound by the promo preview; never discounted. */
    depositAmount: z.number().nonnegative().optional(),
  }),
  z.object({
    kind: z.literal('topup'),
    /** Server-owned card id. The API verifies ownership and derives its template;
     * legacy/imported cards may legitimately have no current template id. */
    cardId: z.string().min(1),
    /** Top-up base amount (before our % fee). Omitted only by `/fee-quote`. */
    amount: z.number().positive().optional(),
  }),
]);
export type CardPaymentClaim = z.infer<typeof CardPaymentClaim>;

/**
 * Single-step outgoing transfer. The seed never leaves the client, so in the
 * real (keyed) flow `signedPayload` carries the client-signed UserOperation
 * (ETH, ERC-4337) or signed TRC-20 transaction (Tron) for the relayer to
 * broadcast. It is optional while the relayer runs in stub mode.
 */
export const TransferCreateInput = z.object({
  walletId: z.string().min(1),
  chain: ChainEnum,
  // Natives allowed here; the route rejects illegal combos via isValidChainToken.
  token: AnyTokenEnum,
  to: z.string().min(1),
  amount: z.string().regex(/^\d+(\.\d+)?$/, 'amount must be a decimal string'),
  signedPayload: z.string().optional(),
  // Links a submit back to a `/prepare` result (the server-built unsigned op).
  prepareId: z.string().optional(),
  // Source address to send from (defaults to the primary for the chain).
  fromAddressId: z.string().optional(),
  cardPayment: CardPaymentClaim.optional(),
  /**
   * Swap deposit claim: when set, the API loads the ChangeHero order and may
   * waive the network fee (1-leg send) if partner margin covers sponsor cost.
   * Invalid claims are ignored and priced as a plain send.
   */
  swapPartnerOrderId: z.string().min(1).optional(),
  /**
   * Destination memo / tag, where the chain has one.
   *
   * Named for what exchanges call it rather than for XRPL's own term, because
   * it is the same field on every chain that has the concept and they all
   * disagree on the word: XRP Ledger says DestinationTag, TON says comment,
   * Monero says payment ID. Binance's own withdrawal form says "Memo/Tag".
   *
   * A STRING, not a number, even though XRP's is a uint32. The value comes
   * from a text input and a tag with leading zeros, or one past 2^53, must
   * survive the trip unchanged to be rejected honestly rather than silently
   * rounded. Each chain parses it into its own form — see xrpDestinationTag.
   *
   * Only meaningful when sending TO a shared address (an exchange). Our own
   * receiving addresses are one per user, so nothing incoming needs a tag.
   */
  memo: z.string().trim().max(64).optional(),
});
export type TransferCreateInput = z.infer<typeof TransferCreateInput>;

/** Failure reason marking an energy/resource failure eligible for a prefilled
 * resend. The retry receives a new live estimate; it does not blindly boost. */
export const TRANSFER_FAIL_INSUFFICIENT_ENERGY = 'insufficient_energy';

/** EIP-7702 authorization tuple the client must sign on first delegation. */
export interface AuthorizationTuple {
  chainId: number;
  address: string;
  nonce: number;
}

/** Response of `POST /v1/transfers/prepare` — the unsigned op to sign. */
export interface TransferPreparedDto {
  prepareId: string;
  chain: 'eth' | 'trx';
  userOpHash: string;
  authorization?: AuthorizationTuple;
  authorizationHash?: string;
  expiresAt: string;
}
